<script setup lang="ts">
/**
 * Angle dial for a dynamic block rotation parameter.
 *
 * ODA does not let us place a rotation grip on the drawing (the parameter's axis is
 * not exposed), and nobody sets a block's rotation by typing degrees anyway — you
 * turn it until it looks right. So the dial is the grip: drag anywhere inside it and
 * the block turns live under the pointer, released as a single undo step.
 *
 * Angles follow the CAD convention: 0° points right, degrees grow counter-clockwise.
 * Screen Y grows downward, hence the sign flips below.
 */
import { ref, computed, onBeforeUnmount } from "vue";
import { Z_INDEX_MODAL } from "./layouts/constants";
import { clampBoxToCanvas } from "./canvasBounds";

const props = defineProps<{
  /** Current value, in degrees. */
  degrees: number;
  /** Parameter name, shown as the dial's title. */
  label: string;
  /** Where the dial was opened from, in viewport coordinates. */
  anchor: { x: number; y: number };
}>();

const emit = defineEmits<{
  /** Continuous, while dragging: apply without recording an undo step. */
  preview: [degrees: number];
  /** End of the gesture: record it, once. */
  commit: [degrees: number];
  close: [];
}>();

const SIZE = 220;
const CENTER = SIZE / 2;
const RADIUS = SIZE / 2 - 18;
/** Live application is throttled: each one costs a full drawing regeneration. */
const APPLY_INTERVAL_MS = 80;

/** Angle shown right now — tracks the pointer, ahead of what has been applied. */
const shown = ref(props.degrees);
const isDragging = ref(false);

const dial = ref<HTMLElement | null>(null);
let lastAppliedAt = 0;
let lastAppliedValue = props.degrees;
const valueAtStart = props.degrees;

/** Anchored to the right of the button that opened it, kept inside the canvas. */
const style = computed(() => {
  const { left, top } = clampBoxToCanvas(
    props.anchor.x + 12,
    props.anchor.y - SIZE / 2,
    SIZE,
    SIZE + 34,
  );
  return { left: `${left}px`, top: `${top}px`, zIndex: Z_INDEX_MODAL + 1 };
});

/** Point on the rim for an angle, in the dial's own coordinates. */
function rimPoint(degrees: number, radius = RADIUS): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.cos(radians),
    y: CENTER - radius * Math.sin(radians),
  };
}

const knob = computed(() => rimPoint(shown.value));

/** Ticks every 15°, longer every 45°. */
const ticks = computed(() =>
  Array.from({ length: 24 }, (_, index) => {
    const angle = index * 15;
    const major = angle % 45 === 0;
    return {
      angle,
      from: rimPoint(angle, RADIUS - (major ? 9 : 5)),
      to: rimPoint(angle, RADIUS),
      major,
    };
  }),
);

/** Filled wedge from 0° to the current angle, so the value reads at a glance. */
const sweep = computed(() => {
  const end = rimPoint(shown.value, RADIUS - 12);
  const start = rimPoint(0, RADIUS - 12);
  const large = normalize(shown.value) > 180 ? 1 : 0;
  const r = RADIUS - 12;
  // Sweep flag 0: SVG sweeps clockwise on screen, which is counter-clockwise in CAD.
  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y} Z`;
});

function normalize(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

/** Angle of a viewport point as seen from the dial's centre. */
function angleFromPointer(event: PointerEvent): number | null {
  const rect = dial.value?.getBoundingClientRect();
  if (!rect) return null;
  const dx = event.clientX - (rect.left + CENTER);
  const dy = event.clientY - (rect.top + CENTER);
  // Screen Y is inverted relative to the CAD convention.
  return normalize((Math.atan2(-dy, dx) * 180) / Math.PI);
}

function onPointerDown(event: PointerEvent): void {
  isDragging.value = true;
  (event.target as HTMLElement).setPointerCapture(event.pointerId);
  event.preventDefault();
  track(event, true);
}

function onPointerMove(event: PointerEvent): void {
  if (!isDragging.value) return;
  track(event, false);
}

function onPointerUp(event: PointerEvent): void {
  if (!isDragging.value) return;
  isDragging.value = false;
  track(event, true);
  emit("commit", shown.value);
}

/**
 * Follow the pointer. The needle moves every frame; the drawing is only updated on
 * a throttle, or when `force` says the gesture just started or ended.
 */
function track(event: PointerEvent, force: boolean): void {
  const angle = angleFromPointer(event);
  if (angle === null) return;
  shown.value = angle;

  const now = performance.now();
  if (!force && now - lastAppliedAt < APPLY_INTERVAL_MS) return;
  if (angle === lastAppliedValue) return;

  lastAppliedAt = now;
  lastAppliedValue = angle;
  emit("preview", angle);
}

function cancel(): void {
  if (shown.value !== valueAtStart) emit("preview", valueAtStart);
  emit("close");
}

onBeforeUnmount(() => {
  isDragging.value = false;
});
</script>

<template>
  <Teleport to="body">
    <!-- Any press outside closes the dial, keeping the angle reached. -->
    <div
      class="dial-backdrop"
      :style="{ zIndex: Z_INDEX_MODAL }"
      @pointerdown.self="emit('close')"
    >
      <div class="dial-panel" :class="{ turning: isDragging }" :style="style">
        <header class="dial-header">
          <span class="dial-title">{{ label }}</span>
          <button type="button" class="dial-reset" @click="cancel">
            {{ $t("cancel") }}
          </button>
        </header>

        <div
          ref="dial"
          class="dial"
          :style="{ width: `${SIZE}px`, height: `${SIZE}px` }"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <svg :width="SIZE" :height="SIZE" :viewBox="`0 0 ${SIZE} ${SIZE}`">
            <circle :cx="CENTER" :cy="CENTER" :r="RADIUS" class="dial-face" />
            <path :d="sweep" class="dial-sweep" />
            <line
              v-for="tick in ticks"
              :key="tick.angle"
              :x1="tick.from.x"
              :y1="tick.from.y"
              :x2="tick.to.x"
              :y2="tick.to.y"
              class="dial-tick"
              :class="{ major: tick.major }"
            />
            <line
              :x1="CENTER"
              :y1="CENTER"
              :x2="knob.x"
              :y2="knob.y"
              class="dial-needle"
            />
            <circle :cx="knob.x" :cy="knob.y" r="10" class="dial-knob" />
            <circle :cx="CENTER" :cy="CENTER" r="3" class="dial-hub" />
          </svg>
          <span class="dial-value">{{ Math.round(shown) }}°</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.dial-backdrop {
  position: fixed;
  inset: 0;
}

.dial-panel {
  position: fixed;
  padding: 6px;
  border-radius: 10px;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  color-scheme: dark;
  transition: opacity 0.12s ease;

  // While turning, the block is moving underneath: fade the whole dial so the
  // result stays visible. The needle, the knob and the value are the brightest
  // things on it, so they remain followable.
  &.turning {
    opacity: 0.6;
    box-shadow: none;
  }
}

.dial-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 6px 6px;
}

.dial-title {
  flex: 1;
  min-width: 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dial-reset {
  border: 0;
  padding: 2px 6px;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.72rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.dial {
  position: relative;
  // Without this a stylus drag scrolls the page instead of turning the dial.
  touch-action: none;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.dial-face {
  fill: rgba(255, 255, 255, 0.04);
  stroke: var(--canvas-border);
}

.dial-sweep {
  fill: rgba(70, 130, 220, 0.22);
}

.dial-tick {
  stroke: rgba(255, 255, 255, 0.25);
  stroke-width: 1;

  &.major {
    stroke: rgba(255, 255, 255, 0.5);
    stroke-width: 1.5;
  }
}

.dial-needle {
  stroke: rgb(70, 130, 220);
  stroke-width: 2;
}

.dial-knob {
  fill: rgb(70, 130, 220);
  stroke: white;
  stroke-width: 1.5;
}

.dial-hub {
  fill: rgba(255, 255, 255, 0.6);
}

.dial-value {
  position: absolute;
  left: 50%;
  top: 62%;
  transform: translateX(-50%);
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  pointer-events: none;
}
</style>
